import { useState, useRef } from "react";
import { Plus, Trash2, GripVertical, X, Info, FileText, Upload, Loader2 } from "lucide-react";
import type { ConversationFlow, ConversationMode, FlowQuestion, ProbingDepth } from "@type/agent";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Input } from "@component/input";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import { cn } from "@util/tailwind";

const PROBING_OPTIONS: { value: ProbingDepth; label: string; description: string }[] = [
    { value: "light", label: "Light", description: "1–2 follow-ups per topic" },
    { value: "medium", label: "Medium", description: "3–4 follow-ups, balanced" },
    { value: "deep", label: "Deep", description: "Exhaustive probing" },
];

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ConversationFlowEditorProps {
    flow: ConversationFlow;
    onChange: (flow: ConversationFlow) => void;
    disabled?: boolean;
    isNew?: boolean;
    onUploadDocument?: (file: File) => Promise<void>;
    onRemoveDocument?: (docId: string) => Promise<void>;
}

export function ConversationFlowEditor({ flow, onChange, disabled, isNew, onUploadDocument, onRemoveDocument }: ConversationFlowEditorProps) {
    const [topicInput, setTopicInput] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mode = flow.conversationMode || "scripted";

    const handleModeChange = (newMode: ConversationMode) => {
        onChange({ ...flow, conversationMode: newMode });
    };

    const handleScriptChange = (field: "openingScript" | "closingScript", value: string) => {
        onChange({ ...flow, [field]: value });
    };

    const handleQuestionChange = (index: number, updates: Partial<FlowQuestion>) => {
        const updated = [...flow.coreQuestions];
        updated[index] = { ...updated[index], ...updates };
        onChange({ ...flow, coreQuestions: updated });
    };

    const addQuestion = () => {
        const newQuestion: FlowQuestion = {
            id: crypto.randomUUID(),
            question: "",
            type: "open_ended",
            required: true,
            order: flow.coreQuestions.length + 1,
        };
        onChange({ ...flow, coreQuestions: [...flow.coreQuestions, newQuestion] });
    };

    const removeQuestion = (index: number) => {
        onChange({ ...flow, coreQuestions: flow.coreQuestions.filter((_, i) => i !== index) });
    };

    const addTopic = () => {
        const trimmed = topicInput.trim();
        if (!trimmed) return;
        const existing = flow.topicAreas || [];
        if (existing.includes(trimmed)) return;
        onChange({ ...flow, topicAreas: [...existing, trimmed] });
        setTopicInput("");
    };

    const removeTopic = (index: number) => {
        const updated = (flow.topicAreas || []).filter((_, i) => i !== index);
        onChange({ ...flow, topicAreas: updated });
    };

    const handleFileUpload = async (file: File) => {
        if (!onUploadDocument) return;
        setIsUploading(true);
        try {
            await onUploadDocument(file);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files[0]);
            e.target.value = "";
        }
    };

    const handleDocRemove = async (docId: string) => {
        if (!onRemoveDocument) return;
        await onRemoveDocument(docId);
    };

    return (
        <div className={disabled ? "space-y-6 pointer-events-none opacity-60" : "space-y-6"}>
            {/* Conversation Mode Toggle */}
            <div>
                <div className="inline-flex items-center rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
                    <button
                        type="button"
                        onClick={() => handleModeChange("scripted")}
                        className={cn(
                            "inline-flex items-center rounded-md px-4 py-2 text-sm font-medium transition-all",
                            mode === "scripted"
                                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        )}
                    >
                        Scripted
                    </button>
                    <button
                        type="button"
                        onClick={() => handleModeChange("open_probe")}
                        className={cn(
                            "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all",
                            mode === "open_probe"
                                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        )}
                    >
                        Open Probe
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px] px-1.5 py-0 leading-4">New</Badge>
                    </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {mode === "scripted"
                        ? "The agent follows a fixed script with ordered questions."
                        : "The agent converses freely, guided by your goals and topics."}
                </p>
            </div>

            {/* Objective — always visible */}
            <Card>
                <CardHeader><CardTitle>Objective</CardTitle></CardHeader>
                <CardContent>
                    <div className="flex items-start gap-1.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Give your agent a clear mission for this campaign. This acts as the agent's guiding context throughout every call.</span>
                    </div>
                    <textarea
                        value={flow.objective || ""}
                        onChange={(e) => onChange({ ...flow, objective: e.target.value })}
                        placeholder="e.g., You are conducting a lead qualification call for our enterprise SaaS product. Your goal is to determine if the prospect has budget, authority, need, and timeline..."
                        rows={4}
                        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-y"
                    />
                </CardContent>
            </Card>

            {/* Knowledge Base — always visible */}
            <Card>
                <CardHeader><CardTitle>Knowledge Base</CardTitle></CardHeader>
                <CardContent>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Upload reference documents (FAQs, product guides, policies) for the agent to draw from during calls.
                    </p>

                    {/* File list */}
                    {(flow.knowledgeDocuments || []).length > 0 && (
                        <div className="space-y-2 mb-4">
                            {(flow.knowledgeDocuments || []).map((doc) => (
                                <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.fileName}</p>
                                        <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleDocRemove(doc.id)}
                                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                        aria-label={`Remove ${doc.fileName}`}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload area */}
                    {isNew ? (
                        <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 p-6">
                            <p className="text-sm text-gray-400">Save your campaign first to upload documents.</p>
                        </div>
                    ) : (
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsDragging(false);
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                    handleFileUpload(e.dataTransfer.files[0]);
                                }
                            }}
                            className={cn(
                                "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer",
                                isDragging
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                    : "border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                            )}
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                        >
                            {isUploading ? (
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin mb-2" />
                            ) : (
                                <Upload className="w-6 h-6 text-gray-400 mb-2" />
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                {isUploading ? "Uploading..." : "Drop files here or click to browse"}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">PDF, DOCX, TXT up to 10MB</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.docx,.txt"
                                onChange={handleFileInputChange}
                                className="hidden"
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Opening Script — always visible */}
            <Card>
                <CardHeader><CardTitle>Opening Script *</CardTitle></CardHeader>
                <CardContent>
                    <textarea
                        value={flow.openingScript}
                        onChange={(e) => handleScriptChange("openingScript", e.target.value)}
                        placeholder="Enter the opening script your agent will use when the call begins..."
                        rows={3}
                        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-none"
                    />
                </CardContent>
            </Card>

            {/* Scripted mode: Core Questions */}
            {mode === "scripted" && (
                <Card>
                    <CardHeader><CardTitle>Core Questions *</CardTitle></CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {flow.coreQuestions.map((question, index) => (
                                <div key={question.id} className="flex gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center text-gray-300"><GripVertical className="w-4 h-4" /></div>
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-start gap-3">
                                            <span className="text-xs font-semibold text-gray-400 bg-white dark:bg-gray-700 rounded px-2 py-1 mt-1">Q{index + 1}</span>
                                            <Input value={question.question} onChange={(e) => handleQuestionChange(index, { question: e.target.value })} placeholder="Enter question..." className="flex-1" />
                                        </div>
                                        <div className="ml-8">
                                            <span className="text-xs text-gray-400">Open Ended · Required</span>
                                        </div>
                                    </div>
                                    <button onClick={() => removeQuestion(index)} className="text-gray-400 hover:text-red-500 transition-colors p-1" aria-label={`Remove question ${index + 1}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            ))}
                            <Button variant="outline" size="sm" onClick={addQuestion}><Plus className="w-4 h-4 mr-1" />Add Question</Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Open Probe mode: Goal, Topics, Depth, Guardrails */}
            {mode === "open_probe" && (
                <>
                    {/* Conversation Goal */}
                    <Card>
                        <CardHeader><CardTitle>Conversation Goal *</CardTitle></CardHeader>
                        <CardContent>
                            <div className="flex items-start gap-1.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
                                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <span>Describe what you want to learn from this call. The agent will use this as its north star while probing naturally.</span>
                            </div>
                            <textarea
                                value={flow.conversationGoal || ""}
                                onChange={(e) => onChange({ ...flow, conversationGoal: e.target.value })}
                                placeholder="e.g., Understand the candidate's leadership experience and how they handle conflict in cross-functional teams..."
                                rows={3}
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-none"
                            />
                        </CardContent>
                    </Card>

                    {/* Topic Areas */}
                    <Card>
                        <CardHeader><CardTitle>Topic Areas *</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                The agent will try to cover all topics but can address them in any order based on the flow of conversation.
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {(flow.topicAreas || []).map((topic, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full px-3 py-1 text-sm"
                                    >
                                        {topic}
                                        <button
                                            type="button"
                                            onClick={() => removeTopic(index)}
                                            className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 transition-colors"
                                            aria-label={`Remove topic: ${topic}`}
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <Input
                                value={topicInput}
                                onChange={(e) => setTopicInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addTopic();
                                    }
                                }}
                                placeholder="Add a topic and press Enter"
                            />
                        </CardContent>
                    </Card>

                    {/* Probing Depth */}
                    <Card>
                        <CardHeader><CardTitle>Probing Depth</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                How aggressively should the agent pursue follow-up questions on each topic?
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                                {PROBING_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => onChange({ ...flow, probingDepth: option.value })}
                                        className={cn(
                                            "rounded-lg border-2 p-4 text-left transition-all",
                                            (flow.probingDepth || "medium") === option.value
                                                ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-400"
                                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                                        )}
                                    >
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{option.label}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{option.description}</p>
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Guardrails & Boundaries */}
                    <Card>
                        <CardHeader><CardTitle>Guardrails & Boundaries</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Optional. Specify topics to avoid, tone constraints, or any limits on the conversation.
                            </p>
                            <textarea
                                value={flow.guardrails || ""}
                                onChange={(e) => onChange({ ...flow, guardrails: e.target.value })}
                                placeholder="e.g., Do not discuss salary expectations. Keep the tone professional but warm. Limit the call to 10 minutes..."
                                rows={3}
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-none"
                            />
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Closing Script — always visible */}
            <Card>
                <CardHeader><CardTitle>Closing Script *</CardTitle></CardHeader>
                <CardContent>
                    <textarea
                        value={flow.closingScript}
                        onChange={(e) => handleScriptChange("closingScript", e.target.value)}
                        placeholder="Enter the closing script your agent will use to end the call..."
                        rows={3}
                        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-y"
                    />
                </CardContent>
            </Card>
        </div>
    );
}
